import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, ShieldCheck, Flame, Droplets } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink, hreflangPair } from "@/components/FiNav";
import { fiHubFaq, fiHubSections } from "@/data/fi-content";

const TITLE = "Asuntoautot ja asuntovaunut Ruotsista – palvelemme suomeksi";
const DESC =
  "AK Husbil & Fritid Kalixissa, 90 km Torniosta. Asuntoautot ja asuntovaunut, tuonti Suomeen, autovero ja siirtolupa – palvelemme suomeksi.";
const URL = "https://www.akhusbilfritid.se/fi";

export const Route = createFileRoute("/fi/")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(fiHubFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "AK Husbil & Fritid, Kalix – asuntoautot ja asuntovaunut" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangPair("/fi", "/")],
    scripts: loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : [],
  }),
  component: FiHubPage,
});

const CHECKS = [
  { icon: <Droplets size={18} />, t: "Kosteusmitattu", d: "Jokainen ajoneuvo mitataan ennen luovutusta." },
  { icon: <Flame size={18} />, t: "Kaasu tarkastettu", d: "Letkut, säädin ja varoitin käydään läpi." },
  { icon: <ShieldCheck size={18} />, t: "Rehellinen kuntoarvio", d: "Kerromme myös puutteet, emme vain hyvää." },
];

function FiHubPage() {
  return (
    <>
      <PageHeader
        label="Suomeksi · Kalix, 90 km Torniosta"
        title={
          <>
            Asuntoauto Ruotsista — <span className="font-italic-display text-accent">suomeksi</span>.
          </>
        }
        sub="Perheyritys Kalixissa vuodesta 2004. Laaja valikoima asuntoautoja ja asuntovaunuja, mittaustulokset avoimesti esillä ja apua koko tuontiprosessiin Suomeen. Soita niin sovitaan aika — palvelemme suomeksi."
      />

      <FiNav />

      <section className="container-edge py-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {CHECKS.map((c) => (
            <div key={c.t} className="rounded-3xl border border-border bg-card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/15 text-accent">{c.icon}</span>
              <h2 className="mt-4 font-display text-xl">{c.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fi/asuntoautot"
            className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Katso asuntoautot <ArrowRight size={14} />
          </Link>
          <Link
            to="/fi/yhteystiedot"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            Ota yhteyttä <ArrowRight size={14} />
          </Link>
          <a
            href="https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
          >
            Koko varasto Fyndanussa <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      <HubEditorial
        eyebrow="Suomalaiselle ostajalle"
        sections={fiHubSections}
        faq={fiHubFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <FiSwedishLink to="/" label="Till startsidan på svenska" />
    </>
  );
}
